import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { getAuth } from 'firebase/auth';
import { getFirestore, collection, query, where, orderBy, getDocs, doc, deleteDoc, getDoc, setDoc } from 'firebase/firestore';
import './GenerateHome.css';

const GenerateHome = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('create'); // 'create', 'saved', or 'settings'
  const [savedScripts, setSavedScripts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [settingsLoading, setSettingsLoading] = useState(false);
  const [settingsSaving, setSettingsSaving] = useState(false);
  const [creatorSettings, setCreatorSettings] = useState({
    toneAndWritingStyle: ''
  });

  const handleYoutubeStoryteller = () => {
    navigate('/generate/youtube-storyteller');
  };

  const loadSavedScripts = async () => {
    setLoading(true);
    try {
      const auth = getAuth();
      const user = auth.currentUser;
      
      if (!user) {
        console.log('User not authenticated');
        setLoading(false);
        return;
      }

      const db = getFirestore();
      // FIX: Korrekte Subcollection - muss mit Cloud Function Pfad übereinstimmen
      const scriptsRef = collection(db, 'content', user.uid, 'youtube-scripts');
      const q = query(
        scriptsRef,
        orderBy('createdAt', 'desc')
      );

      const querySnapshot = await getDocs(q);
      const scripts = [];
      querySnapshot.forEach((doc) => {
        scripts.push({
          id: doc.id,
          ...doc.data()
        });
      });

      console.log(`✅ [GENERATE-HOME] Loaded ${scripts.length} saved scripts`);
      setSavedScripts(scripts);
    } catch (error) {
      console.error('❌ [GENERATE-HOME] Error loading saved scripts:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadCreatorSettings = async () => {
    setSettingsLoading(true);
    try {
      const auth = getAuth();
      const user = auth.currentUser;
      
      if (!user) {
        console.log('User not authenticated');
        setSettingsLoading(false);
        return;
      }

      const db = getFirestore();
      const settingsRef = doc(db, 'content', user.uid, 'creator-settings', 'profile');
      const settingsDoc = await getDoc(settingsRef);

      if (settingsDoc.exists()) {
        const data = settingsDoc.data();
        setCreatorSettings({
          toneAndWritingStyle: data.toneAndWritingStyle || ''
        });
        console.log('✅ [GENERATE-HOME] Loaded creator settings');
      } else {
        console.log('ℹ️ [GENERATE-HOME] No settings found, using defaults');
      }
    } catch (error) {
      console.error('❌ [GENERATE-HOME] Error loading settings:', error);
    } finally {
      setSettingsLoading(false);
    }
  };

  const saveCreatorSettings = async () => {
    setSettingsSaving(true);
    try {
      const auth = getAuth();
      const user = auth.currentUser;
      
      if (!user) {
        alert('You must be logged in to save settings');
        setSettingsSaving(false);
        return;
      }

      const db = getFirestore();
      const settingsRef = doc(db, 'content', user.uid, 'creator-settings', 'profile');
      
      await setDoc(settingsRef, {
        toneAndWritingStyle: creatorSettings.toneAndWritingStyle,
        updatedAt: new Date(),
        userId: user.uid
      }, { merge: true });

      console.log('✅ [GENERATE-HOME] Settings saved successfully');
      alert('✅ Settings saved successfully!');
    } catch (error) {
      console.error('❌ [GENERATE-HOME] Error saving settings:', error);
      alert('Failed to save settings. Please try again.');
    } finally {
      setSettingsSaving(false);
    }
  };

  useEffect(() => {
    if (activeTab === 'saved') {
      loadSavedScripts();
    } else if (activeTab === 'settings') {
      loadCreatorSettings();
    }
  }, [activeTab]);

  const handleScriptClick = (scriptId) => {
    navigate(`/generate/youtube-script/${scriptId}`);
  };

  const handleDeleteScript = async (e, scriptId, scriptTitle) => {
    e.stopPropagation(); // Verhindere Navigation beim Delete
    
    const confirmed = window.confirm(
      `Möchtest du dieses Script wirklich löschen?\n\n"${scriptTitle}"\n\nDiese Aktion kann nicht rückgängig gemacht werden.`
    );
    
    if (!confirmed) return;

    try {
      const auth = getAuth();
      const user = auth.currentUser;
      const db = getFirestore();
      
      const scriptRef = doc(db, 'content', user.uid, 'youtube-scripts', scriptId);
      await deleteDoc(scriptRef);
      
      console.log('✅ [DELETE] Script deleted:', scriptId);
      
      // Aktualisiere die Liste
      setSavedScripts(prev => prev.filter(s => s.id !== scriptId));
      
    } catch (error) {
      console.error('❌ [DELETE] Error deleting script:', error);
      alert('Fehler beim Löschen des Scripts. Bitte versuche es erneut.');
    }
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return 'Unknown date';
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    return new Intl.DateTimeFormat('de-DE', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    }).format(date);
  };

  const getWordCount = (script) => {
    if (script.wordCount) return script.wordCount;
    if (script.fullScript) {
      return script.fullScript.split(/\s+/).length;
    }
    return 0;
  };

  const getEstimatedDuration = (script) => {
    if (script.estimatedDuration) return script.estimatedDuration;
    const wordCount = getWordCount(script);
    const minutes = Math.round(wordCount / 150);
    return `~${minutes} min`;
  };

  return (
    <div className="generate-home-container">
      <div className="generate-home-content">
        <div className="generate-home-header">
          <h1 className="generate-home-title">AI Content Generator</h1>
          <p className="generate-home-subtitle">
            Create viral content and engage your audience with AI-powered tools
          </p>
        </div>

        {/* Tab Navigation */}
        <div className="generate-tabs">
          <button
            className={`generate-tab ${activeTab === 'create' ? 'active' : ''}`}
            onClick={() => setActiveTab('create')}
          >
            <i className="fas fa-plus-circle"></i>
            Create New
          </button>
          <button
            className={`generate-tab ${activeTab === 'saved' ? 'active' : ''}`}
            onClick={() => setActiveTab('saved')}
          >
            <i className="fas fa-bookmark"></i>
            Saved Scripts
            {savedScripts.length > 0 && (
              <span className="generate-tab-badge">{savedScripts.length}</span>
            )}
          </button>
          <button
            className={`generate-tab ${activeTab === 'settings' ? 'active' : ''}`}
            onClick={() => setActiveTab('settings')}
          >
            <i className="fas fa-cog"></i>
            Settings
          </button>
        </div>

        {/* Create Tab */}
        {activeTab === 'create' && (
          <div className="generate-home-tools">
            {/* YouTube Storyteller - Haupttool */}
            <div className="generate-tool-card generate-tool-featured" onClick={handleYoutubeStoryteller}>
              <div className="generate-tool-icon icon-youtube">
                <i className="fab fa-youtube"></i>
              </div>
              <div className="generate-tool-content">
                <div className="generate-tool-header">
                  <h3 className="generate-tool-title">Youtube Storyteller</h3>
                  <span className="generate-tool-badge">Featured</span>
                </div>
                <p className="generate-tool-description">
                  Create viral YouTube scripts and video ideas with AI. Generate engaging storylines, hooks, and content strategies for maximum reach.
                </p>
                <button className="generate-tool-button">
                  Start Creating →
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Saved Tab */}
        {activeTab === 'saved' && (
          <div className="generate-saved-container">
            {loading ? (
              <div className="generate-saved-loading">
                <i className="fas fa-spinner fa-spin"></i>
                <p>Loading saved scripts...</p>
              </div>
            ) : savedScripts.length === 0 ? (
              <div className="generate-saved-empty">
                <i className="fas fa-bookmark"></i>
                <h3>No Saved Scripts Yet</h3>
                <p>Create your first script to see it here</p>
                <button 
                  className="generate-empty-cta"
                  onClick={() => setActiveTab('create')}
                >
                  <i className="fas fa-plus"></i>
                  Create Your First Script
                </button>
              </div>
            ) : (
              <div className="generate-saved-list">
                {savedScripts.map((script, index) => (
                  <div
                    key={script.id}
                    className="generate-saved-item"
                    onClick={() => handleScriptClick(script.id)}
                  >
                    <div className="generate-saved-icon">
                      <i className="fab fa-youtube"></i>
                    </div>
                    
                    <div className="generate-saved-content">
                      <div className="generate-saved-header">
                        <h4 className="generate-saved-title">{script.title || 'Untitled Script'}</h4>
                        <div className="generate-saved-badges">
                          {index < 2 && (
                            <span className="generate-saved-new-badge">
                              NEW
                            </span>
                          )}
                          <span className={`generate-saved-variant variant-${script.variant?.toLowerCase()}`}>
                            {script.variant === 'A' ? '🅰️ Script A' : '🅱️ Script B'}
                          </span>
                        </div>
                      </div>

                      <div className="generate-saved-stats">
                        <span className="generate-saved-stat">
                          <i className="far fa-clock"></i>
                          {getEstimatedDuration(script)}
                        </span>
                        <span className="generate-saved-stat">
                          <i className="fas fa-file-alt"></i>
                          {getWordCount(script).toLocaleString()} words
                        </span>
                        {script.reelSegments && script.reelSegments.length > 0 && (
                          <span className="generate-saved-stat">
                            <i className="fas fa-mobile-alt"></i>
                            {script.reelSegments.length} reels
                          </span>
                        )}
                        <span className="generate-saved-stat">
                          <i className="far fa-calendar"></i>
                          {formatDate(script.createdAt)}
                        </span>
                      </div>

                      {script.hook && (
                        <div className="generate-saved-hook">
                          <span className="generate-saved-hook-label">🎣 Hook:</span>
                          <p className="generate-saved-hook-text">
                            "{script.hook.substring(0, 150)}{script.hook.length > 150 ? '...' : ''}"
                          </p>
                        </div>
                      )}
                    </div>

                    <div className="generate-saved-actions">
                      <button
                        className="generate-saved-btn generate-saved-btn-delete"
                        onClick={(e) => handleDeleteScript(e, script.id, script.title || 'Untitled Script')}
                        title="Script löschen"
                      >
                        <i className="fas fa-trash-alt"></i>
                      </button>
                      <div className="generate-saved-btn generate-saved-btn-open">
                        <i className="fas fa-chevron-right"></i>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Settings Tab */}
        {activeTab === 'settings' && (
          <div className="generate-settings-container">
            {settingsLoading ? (
              <div className="generate-saved-loading">
                <i className="fas fa-spinner fa-spin"></i>
                <p>Loading settings...</p>
              </div>
            ) : (
              <div className="generate-settings-content">
                <div className="generate-settings-header">
                  <div className="generate-settings-header-icon">
                    <i className="fas fa-user-cog"></i>
                  </div>
                  <div>
                    <h2 className="generate-settings-title">Creator Settings</h2>
                    <p className="generate-settings-subtitle">Customize your content creation preferences</p>
                  </div>
                </div>

                <div className="generate-settings-section">
                  <div className="generate-settings-field">
                    <label className="generate-settings-label">
                      <span className="generate-settings-label-text">
                        <i className="fas fa-pen-fancy"></i>
                        Tone & Writing Style
                      </span>
                      <span className="generate-settings-label-hint">
                        Describe your preferred writing tone and style for AI-generated content
                      </span>
                    </label>
                    <textarea
                      className="generate-settings-textarea"
                      placeholder="Example: Professional yet conversational, witty with subtle humor, engaging storytelling with relatable examples..."
                      value={creatorSettings.toneAndWritingStyle}
                      onChange={(e) => setCreatorSettings({
                        ...creatorSettings,
                        toneAndWritingStyle: e.target.value
                      })}
                      rows={6}
                    />
                    <div className="generate-settings-field-footer">
                      <span className="generate-settings-char-count">
                        {creatorSettings.toneAndWritingStyle.length} characters
                      </span>
                    </div>
                  </div>

                  <div className="generate-settings-actions">
                    <button
                      className="generate-settings-btn generate-settings-btn-save"
                      onClick={saveCreatorSettings}
                      disabled={settingsSaving}
                    >
                      {settingsSaving ? (
                        <>
                          <i className="fas fa-spinner fa-spin"></i>
                          Saving...
                        </>
                      ) : (
                        <>
                          <i className="fas fa-save"></i>
                          Save Settings
                        </>
                      )}
                    </button>
                  </div>
                </div>

                <div className="generate-settings-info">
                  <div className="generate-settings-info-icon">
                    <i className="fas fa-lightbulb"></i>
                  </div>
                  <div className="generate-settings-info-content">
                    <h4 className="generate-settings-info-title">How this helps</h4>
                    <p className="generate-settings-info-text">
                      Your tone and writing style preferences will be used by the AI to generate more personalized content that matches your unique voice and brand identity.
                    </p>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default GenerateHome;

